import validKennitala from '@hugsmidjan/qj/validKennitala';
import { FormSlices as PrismicFormSlices, FormSlicesForm_Input } from 'prismic-types';
import { z, ZodType } from 'zod';

import { asText } from 'components/rich-text/RichText';

import { stringToNumber } from 'utils/stringToNumber';

const checkBoxLiteral = z.literal('checked');
export const checkBoxLiteralValue = checkBoxLiteral.value;

const payloadLimit =
  stringToNumber(process.env.NEXT_VERCEL_PAYLOAD_LIMIT ?? '4000000') ?? 4000000;

const textInputTypeToZodPrimitive = (inputType?: string | null) => {
  switch (inputType) {
    case 'kennitala':
      return z
        .preprocess((val) => String(val).replace(/\s|-/g, ''), z.string().trim())
        .refine(
          (val) => validKennitala(val),
          () => ({ message: 'invalid_ssn' })
        );
    case 'email':
      return z.string().email();
    case 'tel':
      return z
        .preprocess(
          (val) => String(val).replace(/\s|-/g, ''),
          z.string().trim().length(7)
        )
        .refine(
          (val) => val.match(/\d{7}/),
          () => ({ message: 'invalid_tel' })
        );
    case 'number':
      return z.preprocess(
        (val) => parseInt(String(val).replace(/[^0-9]+/g, '')),
        z.number()
      );
    default:
      return z.string();
  }
};

const formInputSliceToZodPrimitive = (s: FormSlicesForm_Input) => {
  if (!s.variation) {
    return z.nullable(z.string());
  }

  const type = s.variation.__typename;

  if (type === 'FormSlicesForm_inputDefault') {
    return textInputTypeToZodPrimitive(s.variation.primary?.type);
  } else if (type === 'FormSlicesForm_inputFileinput') {
    return z.any().refine(
      (val) => {
        const size = Number(val);
        return size < payloadLimit;
      },
      () => ({ message: 'form_file_too_big' })
    );
  } else if (type === 'FormSlicesForm_inputSelectinput') {
    return z.string();
  }

  return z.nullable(z.string());
};

const isRquired = (s: PrismicFormSlices, a: ZodType) => {
  const primary = s.variation?.primary;
  if (primary && 'required' in primary) {
    const required = Boolean(primary.required);
    return required ? a : z.optional(a);
  } else {
    return z.optional(a);
  }
};

const formSliceToZodPrimitive = (s: PrismicFormSlices) => {
  switch (s.type) {
    case 'form_input':
      return isRquired(s, formInputSliceToZodPrimitive(s as FormSlicesForm_Input));
    case 'checkbox_input':
      // checkbox input is a literal and can not be anything but `checked`
      // CRM expects a boolean but changing to a boolean literal breaks `isRequired`
      // thus this mess...
      return z.preprocess(
        (value) => {
          // preprocess to a string to be validated against the literal `checked`
          if (typeof value === 'string' && value !== checkBoxLiteralValue) {
            const mapLiteral: Record<string, string | undefined> = {
              false: undefined,
              true: 'checked',
            };
            return mapLiteral[value];
          } else {
            return value ? 'checked' : undefined;
          }
        }, // and then we transform to a boolean for CRM
        isRquired(s, checkBoxLiteral).transform((value) => value === checkBoxLiteralValue)
      );
    default:
      return z.nullable(z.string());
  }
};

export const constructFormValidation = (data?: Array<PrismicFormSlices>) => {
  if (!data || data.length === 0) {
    return z.object({});
  }

  const object = data.reduce((acc, curr) => {
    const key = asText(curr.variation?.primary?.name);
    acc[key] = formSliceToZodPrimitive(curr);

    return acc;
  }, {} as Record<string, ZodType>);

  return z.object(object);
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type InferredObjectType = z.ZodObject<{}, 'strip', z.ZodTypeAny, {}, {}>;
